import { Edge, Node } from '@xyflow/react';

import { ConnectionValidationResult } from '../types/connections';
import { NODE_TYPE } from '../types/node';

/**
 * ForLoop connection validation uses the same result type as general connection validation.
 * Previously had redundant `canConnect` (same as `isValid`) and `reason` (same as `suggestion`).
 */
export type ForLoopConnectionValidationResult = ConnectionValidationResult;

export class ForLoopConnectionValidator {
  private nodes: Node[];
  private edges: Edge[];

  constructor(nodes: Node[], edges: Edge[]) {
    this.nodes = nodes;
    this.edges = edges;
  }

  validateForLoopEntry(
    sourceNodeId: string,
    forLoopNodeId: string
  ): ForLoopConnectionValidationResult {
    const forLoopNode = this.nodes.find(n => n.id === forLoopNodeId);
    const sourceNode = this.nodes.find(n => n.id === sourceNodeId);

    if (!forLoopNode || forLoopNode.type !== NODE_TYPE.FOR_LOOP) {
      return {
        isValid: false,
        error: 'Invalid target node type',
        suggestion: 'Target is not a ForLoop node',
      };
    }

    if (!sourceNode) {
      return {
        isValid: false,
        error: 'Source node does not exist',
        suggestion: 'Source node not found',
      };
    }

    // Note: Max connections validation is handled by the general validateConnectionCounts()
    // in useConnectionValidation.ts, which respects the maxConnections config from ForLoopNodeType

    if (this.isNodeInsideForLoop(sourceNodeId, forLoopNodeId)) {
      return {
        isValid: false,
        error: 'Invalid internal to external connection',
        suggestion: 'Cannot connect from inside ForLoop to ForLoop input',
      };
    }

    return { isValid: true };
  }

  validateForLoopExit(
    forLoopNodeId: string,
    targetNodeId: string
  ): ForLoopConnectionValidationResult {
    const forLoopNode = this.nodes.find(n => n.id === forLoopNodeId);
    const targetNode = this.nodes.find(n => n.id === targetNodeId);

    if (!forLoopNode || forLoopNode.type !== NODE_TYPE.FOR_LOOP) {
      return {
        isValid: false,
        error: 'Invalid source node type',
        suggestion: 'Source is not a ForLoop node',
      };
    }

    if (!targetNode) {
      return {
        isValid: false,
        error: 'Target node does not exist',
        suggestion: 'Target node not found',
      };
    }

    if (this.isNodeInsideForLoop(targetNodeId, forLoopNodeId)) {
      return {
        isValid: false,
        error: 'Invalid external to internal connection',
        suggestion: 'Cannot connect from ForLoop to its internal nodes',
      };
    }

    if (targetNode.type === NODE_TYPE.FOR_LOOP && targetNodeId !== forLoopNodeId) {
      return this.validateForLoopEntry(forLoopNodeId, targetNodeId);
    }

    return { isValid: true };
  }

  validateInternalForLoopConnection(
    sourceNodeId: string,
    targetNodeId: string,
    forLoopNodeId: string
  ): ForLoopConnectionValidationResult {
    const sourceNode = this.nodes.find(n => n.id === sourceNodeId);
    const targetNode = this.nodes.find(n => n.id === targetNodeId);

    if (!sourceNode || !targetNode) {
      return {
        isValid: false,
        error: 'Node does not exist',
        suggestion: 'Source or target node not found',
      };
    }

    const isSourceInternal = this.isNodeInsideForLoop(sourceNodeId, forLoopNodeId);
    const isTargetInternal = this.isNodeInsideForLoop(targetNodeId, forLoopNodeId);

    if (!isSourceInternal || !isTargetInternal) {
      return {
        isValid: false,
        error: 'Invalid cross-boundary connection',
        suggestion: 'Both nodes must be inside the same ForLoop',
      };
    }

    if (this.wouldCreateCycle(sourceNodeId, targetNodeId)) {
      return {
        isValid: false,
        error: 'Circular dependency detected',
        suggestion: 'Connection would create a cycle',
      };
    }

    return { isValid: true };
  }

  validateExternalToInternalConnection(
    sourceNodeId: string,
    targetNodeId: string
  ): ForLoopConnectionValidationResult {
    const targetNode = this.nodes.find(n => n.id === targetNodeId);
    if (!targetNode || !targetNode.parentId) {
      return {
        isValid: false,
        error: 'Invalid connection target',
        suggestion: 'Target is not inside a ForLoop',
      };
    }

    const forLoopNode = this.nodes.find(n => n.id === targetNode.parentId);
    if (!forLoopNode || forLoopNode.type !== NODE_TYPE.FOR_LOOP) {
      return {
        isValid: false,
        error: 'Invalid parent node type',
        suggestion: 'Target parent is not a ForLoop',
      };
    }

    const isSourceExternal = !this.isNodeInsideForLoop(sourceNodeId, targetNode.parentId);
    if (!isSourceExternal) {
      return {
        isValid: false,
        error: 'Invalid internal connection',
        suggestion: 'Source must be external to ForLoop',
      };
    }

    // Block direct external to internal connections - must go through ForLoop node
    return {
      isValid: false,
      error: 'Connect to the ForLoop node instead',
      suggestion: 'Cannot connect directly to nodes inside ForLoop',
    };
  }

  validateInternalToExternalConnection(
    sourceNodeId: string,
    targetNodeId: string
  ): ForLoopConnectionValidationResult {
    const sourceNode = this.nodes.find(n => n.id === sourceNodeId);
    if (!sourceNode || !sourceNode.parentId) {
      return {
        isValid: false,
        error: 'Invalid connection source',
        suggestion: 'Source is not inside a ForLoop',
      };
    }

    const isTargetExternal = !this.isNodeInsideForLoop(targetNodeId, sourceNode.parentId);
    if (!isTargetExternal) {
      return {
        isValid: false,
        error: 'Invalid internal connection',
        suggestion: 'Target must be external to ForLoop',
      };
    }

    // Block direct internal to external connections - must go through ForLoop node
    return {
      isValid: false,
      error: 'Connect from the ForLoop node instead',
      suggestion: 'Cannot connect from inside ForLoop directly to external nodes',
    };
  }

  private isNodeInsideForLoop(nodeId: string, forLoopNodeId: string): boolean {
    const node = this.nodes.find(n => n.id === nodeId);
    return node?.parentId === forLoopNodeId;
  }

  private wouldCreateCycle(sourceNodeId: string, targetNodeId: string): boolean {
    const visited = new Set<string>();
    const stack = [targetNodeId];

    while (stack.length > 0) {
      const currentNodeId = stack.pop();
      if (!currentNodeId) continue;

      if (currentNodeId === sourceNodeId) {
        return true;
      }

      if (visited.has(currentNodeId)) {
        continue;
      }

      visited.add(currentNodeId);

      const outgoingEdges = this.edges.filter(edge => edge.source === currentNodeId);
      for (const edge of outgoingEdges) {
        if (!visited.has(edge.target)) {
          stack.push(edge.target);
        }
      }
    }

    return false;
  }

  static validateConnection(
    sourceNodeId: string,
    targetNodeId: string,
    nodes: Node[],
    edges: Edge[]
  ): ForLoopConnectionValidationResult {
    const validator = new ForLoopConnectionValidator(nodes, edges);

    const sourceNode = nodes.find(n => n.id === sourceNodeId);
    const targetNode = nodes.find(n => n.id === targetNodeId);

    if (!sourceNode || !targetNode) {
      return {
        isValid: false,
        error: 'Node does not exist',
        suggestion: 'Source or target node not found',
      };
    }

    if (sourceNode.type === NODE_TYPE.FOR_LOOP && targetNode.type !== NODE_TYPE.FOR_LOOP) {
      return validator.validateForLoopExit(sourceNodeId, targetNodeId);
    }

    if (sourceNode.type !== NODE_TYPE.FOR_LOOP && targetNode.type === NODE_TYPE.FOR_LOOP) {
      return validator.validateForLoopEntry(sourceNodeId, targetNodeId);
    }

    if (sourceNode.parentId && targetNode.parentId) {
      if (sourceNode.parentId === targetNode.parentId) {
        return validator.validateInternalForLoopConnection(
          sourceNodeId,
          targetNodeId,
          sourceNode.parentId
        );
      }
    }

    if (sourceNode.parentId && !targetNode.parentId) {
      return validator.validateInternalToExternalConnection(sourceNodeId, targetNodeId);
    }

    if (!sourceNode.parentId && targetNode.parentId) {
      return validator.validateExternalToInternalConnection(sourceNodeId, targetNodeId);
    }

    return { isValid: true };
  }
}
